import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { sessionId } from '../lib/attribution';

/**
 * Enregistre l'arrivée (POST /api/visit) pour l'admin, une fois par session. Pas de notification :
 * Telegram / OpenPro ne partent qu'au clic vers le paiement.
 * Envoyé après ~1,5 s, le temps que la langue soit fixée (Canada → FR) : la notif dit quelle version il voit.
 * `?nonotif=1` dans l'URL coupe les notifs sur cet appareil (pour Gaut), `?nonotif=0` les remet.
 */

const SENT_KEY = 'openbro_visit_sent';
const MUTE_KEY = 'openbro_visit_mute';
const PRIVATE_PATHS = /^\/(admin|dashboard|formateur|programme|success)/;
const PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];

export default function VisitBeacon() {
  const location = useLocation();
  const { lang } = useLang();
  const langRef = useRef(lang);
  langRef.current = lang;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    try {
      if (query.get('nonotif') === '1') localStorage.setItem(MUTE_KEY, '1');
      if (query.get('nonotif') === '0') localStorage.removeItem(MUTE_KEY);
      if (localStorage.getItem(MUTE_KEY) === '1' || localStorage.getItem('token')) return undefined;
      if (sessionStorage.getItem(SENT_KEY) || PRIVATE_PATHS.test(location.pathname)) return undefined;
    } catch {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try { sessionStorage.setItem(SENT_KEY, '1'); } catch { /* ignoré */ }
      const body = {
        page: `${location.pathname}${location.search}`.slice(0, 200),
        referrer: document.referrer || '',
        lang: langRef.current,
        // Même identifiant que le clic et le checkout : /admin/analytics suit le visiteur d'une étape à l'autre.
        sid: sessionId(),
        language: navigator.language || '',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        screen: `${window.screen.width}x${window.screen.height}`,
      };
      PARAMS.forEach((key) => { if (query.get(key)) body[key] = query.get(key).slice(0, 120); });
      fetch('/api/visit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    }, 1500);
    return () => window.clearTimeout(timer);
    // Une seule fois, à l'arrivée : ni la langue ni la page suivante ne relancent l'envoi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      if (localStorage.getItem(MUTE_KEY) === '1' || localStorage.getItem('token')) return undefined;
      if (PRIVATE_PATHS.test(location.pathname)) return undefined;
    } catch {
      return undefined;
    }
    let sid = sessionStorage.getItem('openbro_sid');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('openbro_sid', sid);
    }
    const seen = new Set();
    let section = '';
    let videoPct = 0;
    const sections = [...document.querySelectorAll('section[id]')];
    const watch = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
        section = entry.target.id;
        seen.add(section);
      });
    }, { threshold: [0.35] });
    sections.forEach((node) => watch.observe(node));
    const readVideo = () => {
      const video = document.querySelector('#film-top video, .obf video');
      if (!video || !video.duration) return;
      videoPct = Math.min(100, Math.round((video.currentTime / video.duration) * 100));
    };
    const send = (action) => {
      readVideo();
      const posthog = window.posthog;
      if (posthog && typeof posthog.startSessionRecording === 'function') posthog.startSessionRecording();
      const ph = posthog && typeof posthog.get_session_id === 'function' ? String(posthog.get_session_id() || '') : '';
      const replay = posthog && typeof posthog.get_session_replay_url === 'function' ? String(posthog.get_session_replay_url() || '') : '';
      const body = JSON.stringify({
        sid,
        action,
        page: location.pathname,
        section,
        video: videoPct,
        path: [...seen].slice(0, 12),
        ph: ph.slice(0, 80),
        replay: replay.slice(0, 240),
      });
      if (action === 'leave' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/analytics/presence', new Blob([body], { type: 'application/json' }));
        return;
      }
      fetch('/api/analytics/presence', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    };
    send('ping');
    const timer = window.setInterval(() => send('ping'), 30000);
    const leave = () => send('leave');
    window.addEventListener('pagehide', leave);
    return () => {
      watch.disconnect();
      window.clearInterval(timer);
      window.removeEventListener('pagehide', leave);
    };
  }, [location.pathname]);

  return null;
}
